import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryDown, mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import LoyaltyProductLogo from 'loyalty/components/LoyaltyProductLogo'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import LoyaltyInfiniteBrandLogo from 'loyalty/components/LoyaltyInfiniteBrandLogo'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import useRebrandVersion from 'hooks/useRebrandVersion'

const HeroSection = styled.div`
  padding: ${rem(12)} 0 ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} 0;
  }

  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const HeroBody = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    gap: ${rem(40)};
  }

  ${mediaQueryDown.tablet} {
    max-width: ${rem(400)};
  }

  ${mediaQueryOnly.tablet} {
    margin-inline: auto;
  }
`

const PanelColumn = styled.div`
  flex: 0 0 auto;
  width: 100%;
  max-width: ${rem(400)};

  ${mediaQueryOnly.tablet} {
    margin-inline: auto;
  }
`

const BackLink = styled(ClickableLink)`
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
  }
`

interface Props {
  panel: ReactNode
}

function LoyaltyPurchasePointsHeroSection(props: Props) {
  const { panel } = props
  const intl = useIntl()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const productLogoHeight = useRebrandVersion() === 'rebrand' ? 20 : 32

  const heading = intl.formatMessage({
    id: '8PY9LRMUH6-9aI0teTdX',
    defaultMessage: 'Top up your Société Points',
    description: 'Hero heading on the Société Buy Points landing page',
  })

  const subhead = intl.formatMessage({
    id: 'dsUfJQ8Gv9NuFVf85uuo',
    defaultMessage: 'Buy more points and start redeeming sooner – on stays, flights and more.',
    description: 'Body copy on the buy-points CTA card shown on the Société account benefits tab',
  })

  const membershipLabel = intl.formatMessage({
    id: '500yM7XxpMsbDTAifhMo',
    defaultMessage: 'Your Membership',
    description: 'Back link from the Société Buy Points hero to the membership landing page',
  })

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <Group direction="vertical" desktopDirection="horizontal" gap={32} desktopGap={40} verticalAlign="center">
          <HeroBody>
            <BackLink to={`/${LOYALTY_LANDING_PAGE}`}>
              <BodyTextBlock variant="medium" startIcon={<LineAngleLeftIcon />}>
                {membershipLabel}
              </BodyTextBlock>
            </BackLink>
            <Group direction="vertical" gap={20} horizontalAlign="start">
              {isInfiniteTier && <LoyaltyInfiniteBrandLogo height={28} />}
              {!isInfiniteTier && <LoyaltyProductLogo height={productLogoHeight} />}
              <VerticalSpacer gap={8}>
                <HeadingTextBlock variant="heading2">{heading}</HeadingTextBlock>
                <BodyTextBlock variant="large">{subhead}</BodyTextBlock>
              </VerticalSpacer>
            </Group>
          </HeroBody>
          <PanelColumn>{panel}</PanelColumn>
        </Group>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyPurchasePointsHeroSection
